// src/components/InnovaFastLobby.jsx
import React, { useState, useEffect } from 'react';
import { Rocket, Plus, Calendar, ArrowRight, BarChart3, Clock, CheckCircle2, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getSafeToken } from '../utils/tokenHelper';

// Helper function para obtener la URL del backend con fallback robusto
const getBackendUrl = () => {
  const envUrl = import.meta.env.VITE_BACKEND_URL;
  // Si la variable de entorno no está definida, usar la URL de producción
  return envUrl && envUrl !== 'undefined' ? envUrl : 'https://edutechpulselab.com/api';
};

const InnovaFastLobby = ({ onStartNew, onResumeSession }) => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    loadUserSessions();
  }, []);

  const loadUserSessions = async () => {
    try {
      setLoading(true);
      const token = getSafeToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${getBackendUrl()}/innovafast/user-sessions`, {
        method: 'GET',
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      } else {
        console.error('Error al cargar sesiones:', response.statusText);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para eliminar una sesión
  const deleteSession = async (sessionId, event) => {
    // Prevenir que se active el evento de continuar
    event.stopPropagation();
    
    if (!confirm('¿Estás seguro de que deseas eliminar este proyecto? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const token = getSafeToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${getBackendUrl()}/innovafast/session/${sessionId}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        // Actualizar la lista local eliminando la sesión
        setSessions(prevSessions => 
          prevSessions.filter(session => session.id !== sessionId)
        );
      } else {
        console.error('Error deleting session:', response.statusText);
        alert('Error al eliminar el proyecto. Inténtalo de nuevo.');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('Error al eliminar el proyecto. Inténtalo de nuevo.');
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      'Descubrimiento': 'bg-emerald-100 text-emerald-700',
      'Definición': 'bg-blue-100 text-blue-700',
      'Ideación': 'bg-purple-100 text-purple-700',
      'Prototipado': 'bg-orange-100 text-orange-700',
      'Validación': 'bg-red-100 text-red-700'
    };
    return colors[stage] || 'bg-gray-100 text-gray-700';
  };

  const getStageProgress = (stage) => {
    const stages = ['Descubrimiento', 'Definición', 'Ideación', 'Prototipado', 'Validación'];
    const currentIndex = stages.indexOf(stage);
    return ((currentIndex + 1) / stages.length) * 100;
  };

  const formatLastActivity = (date) => {
    const now = new Date();
    const activityDate = new Date(date);
    const diffTime = Math.abs(now - activityDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Hace 1 día';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    if (diffDays < 30) return `Hace ${Math.ceil(diffDays / 7)} semanas`;
    return activityDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const activeSessions = sessions.filter(session => session.stage !== 'Completado');
  const completedSessions = sessions.filter(session => session.stage === 'Completado');

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-100 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-semibold text-gray-800">InnovaFast</h1>
          </div>
          <p className="text-lg text-gray-600 mb-2">
            ¡Hola, {user?.name || user?.email || 'Innovador'}! 👋
          </p>
          <p className="text-gray-500">
            Acelera tu proceso de innovación con metodología estructurada
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            <span className="ml-3 text-gray-600">Cargando tus proyectos...</span>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Acción Principal - Nueva Validación */}
            <div className="bg-white rounded-2xl shadow-lg border border-emerald-100 p-6 md:p-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Plus className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">
                  Nueva Validación de Innovación
                </h2>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Inicia un nuevo proceso de innovación guiado por nuestra metodología de 5 etapas
                </p>
                <button
                  onClick={onStartNew}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-xl font-semibold flex items-center gap-2 mx-auto transition-all duration-200 transform hover:scale-105"
                >
                  <Plus className="w-5 h-5" />
                  Comenzar Nueva Validación
                </button>
              </div>
            </div>

            {/* Proyectos Existentes */}
            {(activeSessions.length > 0 || completedSessions.length > 0) && (
              <div className="bg-white rounded-2xl shadow-lg border p-6 md:p-8">
                <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-600" />
                  Tus Proyectos de Innovación
                </h3>

                {/* Tabs */}
                <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
                  <button
                    onClick={() => setActiveTab('active')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      activeTab === 'active'
                        ? 'bg-white text-emerald-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    En Progreso ({activeSessions.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('completed')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      activeTab === 'completed'
                        ? 'bg-white text-emerald-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    Completados ({completedSessions.length})
                  </button>
                </div>

                {/* Lista de Sesiones */}
                <div className="space-y-4">
                  {activeTab === 'active' && activeSessions.length === 0 && (
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">No tienes proyectos en progreso</p>
                      <p className="text-sm text-gray-400">¡Comienza tu primera validación!</p>
                    </div>
                  )}

                  {activeTab === 'completed' && completedSessions.length === 0 && (
                    <div className="text-center py-8">
                      <CheckCircle2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">Aún no has completado proyectos</p>
                      <p className="text-sm text-gray-400">Termina tu primera validación para verla aquí</p>
                    </div>
                  )}

                  {(activeTab === 'active' ? activeSessions : completedSessions).map((session) => (
                    <div
                      key={session.id}
                      className="border border-gray-200 rounded-xl p-4 md:p-5 hover:shadow-md transition-all duration-200 hover:border-emerald-200"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-semibold text-gray-800 text-lg">
                              {session.title || `Proyecto ${session.id.slice(-6)}`}
                            </h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStageColor(session.stage)}`}>
                              {session.stage}
                            </span>
                          </div>

                          <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatLastActivity(session.updatedAt)}
                            </div>
                            <div className="flex items-center gap-1">
                              <BarChart3 className="w-4 h-4" />
                              Etapa {Math.ceil(getStageProgress(session.stage) / 20)} de 5
                            </div>
                          </div>

                          {/* Barra de Progreso */}
                          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                            <div 
                              className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${getStageProgress(session.stage)}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500">
                            {getStageProgress(session.stage).toFixed(0)}% completado
                          </p>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* Botón Eliminar */}
                          <button
                            onClick={(e) => deleteSession(session.id, e)}
                            className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-colors duration-200 group"
                            title="Eliminar proyecto"
                          >
                            <Trash2 className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
                          </button>
                          
                          {/* Botón Continuar */}
                          <button
                            onClick={() => onResumeSession(session.id)}
                            className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors duration-200"
                          >
                            {activeTab === 'active' ? 'Continuar' : 'Ver Resumen'}
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InnovaFastLobby;